// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <div>

    <!-- ── 1. Greeting ───────────────────────────────────────────────────── -->
    <div class="greeting-row">
      <div>
        <h1 class="greeting-title">{{ greeting }}, {{ displayName }}</h1>
        <p class="greeting-sub">{{ greetingSub }}</p>
      </div>
    </div>

    <!-- ── 2. Limit bars ─────────────────────────────────────────────────── -->
    <a-card size="small" style="margin-bottom: 12px;" :loading="loadingAccount">
      <div class="limits-row">
        <div v-for="item in limitItems" :key="item.key" class="limit-item">
          <div class="limit-header">
            <span class="limit-label" :title="$t(item.labelKey)">{{ $t(item.labelKey) }}</span>
            <span class="limit-used" :class="getUsedClass(item.pct)">{{ item.usedDisplay }}</span>
          </div>
          <a-progress
            :percent="item.pct"
            :show-info="false"
            size="small"
            :stroke-color="getBarColor(item.pct)"
            trail-color="#f0f0f0"
          />
          <div class="limit-footer">
            <span>{{ item.availDisplay }} left</span>
            <span class="limit-max">{{ item.limitDisplay === 'Unlimited' ? '∞' : '/ ' + item.limitDisplay }}</span>
          </div>
        </div>
      </div>
    </a-card>

    <!-- ── 3. Resource count pills ───────────────────────────────────────── -->
    <a-card size="small" style="margin-bottom: 12px;" :loading="loadingCounts">
      <div class="counts-row">
        <router-link
          v-for="res in resourceCounts"
          :key="res.key"
          :to="res.path"
          class="count-pill"
        >
          <component :is="res.icon" class="count-icon" />
          <span class="count-value">{{ res.count }}</span>
          <span class="count-label">{{ $t(res.labelKey) }}</span>
        </router-link>
      </div>
    </a-card>

    <!-- ── 4. Mini usage chart (hour-based metrics only) ─────────────────── -->
    <a-card
      size="small"
      style="margin-bottom: 12px;"
      v-if="'listUsageRecords' in $store.getters.apis"
    >
      <template #title>
        <BarChartOutlined /> {{ $t('label.usage.hours') || 'Usage This Month' }} — {{ currentMonthLabel }}
      </template>
      <template #extra>
        <router-link :to="{ path: '/usage_metrics' }">{{ $t('label.details') || 'Full report' }}</router-link>
      </template>
      <a-spin :spinning="loadingUsage">
        <div v-if="hasUsageData" style="position: relative; height: 180px;">
          <canvas ref="usageChartCanvas"></canvas>
        </div>
        <a-empty v-else :description="'No usage data for ' + currentMonthLabel" style="padding: 12px 0;" />
      </a-spin>
    </a-card>

    <!-- ── 5. Main content ───────────────────────────────────────────────── -->

    <!-- Tabbed resource table: full width -->
    <a-card size="small" :body-style="{ padding: 0 }" style="margin-bottom: 12px;">
          <a-tabs v-model:activeKey="activeTab" size="small" style="padding: 0 12px;">

            <!-- Instances tab -->
            <a-tab-pane
              key="vms"
              v-if="'listVirtualMachines' in $store.getters.apis"
            >
              <template #tab>
                <CloudServerOutlined /> {{ $t('label.instances') }}
                <a-badge :count="vmTotal" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="vms"
                :columns="vmColumns"
                :loading="loadingVms"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/vm/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                  <template v-else-if="column.key === 'resources'">
                    <a-tag>{{ record.cpunumber }} vCPU</a-tag>
                    <a-tag>{{ formatMemory(record.memory) }}</a-tag>
                  </template>
                  <template v-else-if="column.key === 'ipaddress'">
                    <span v-for="nic in (record.nic || [])" :key="nic.id">
                      <span v-if="nic.ipaddress">{{ nic.ipaddress }}<br/></span>
                    </span>
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/vm' }">{{ $t('label.view.all') || 'View all' }} {{ vmTotal }} {{ $t('label.instances') }}</router-link>
              </div>
            </a-tab-pane>

            <!-- Kubernetes tab -->
            <a-tab-pane
              key="k8s"
              v-if="'listKubernetesClusters' in $store.getters.apis"
            >
              <template #tab>
                <ClusterOutlined /> {{ $t('label.kubernetes') || 'Kubernetes' }}
                <a-badge :count="k8sTotal" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="k8sClusters"
                :columns="k8sColumns"
                :loading="loadingK8s"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/kubernetes/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                  <template v-else-if="column.key === 'size'">
                    {{ record.size }} node{{ record.size !== 1 ? 's' : '' }}
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/kubernetes' }">{{ $t('label.view.all') || 'View all' }} {{ k8sTotal }} {{ $t('label.kubernetes.cluster') }}</router-link>
              </div>
            </a-tab-pane>

            <!-- Autoscale groups tab -->
            <a-tab-pane
              key="autoscale"
              v-if="'listAutoScaleVmGroups' in $store.getters.apis"
            >
              <template #tab>
                <SyncOutlined /> {{ $t('label.autoscale.vmgroup') || 'Autoscale' }}
                <a-badge :count="autoscaleTotal" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="autoscaleGroups"
                :columns="autoscaleColumns"
                :loading="loadingAutoscale"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/autoscalevmgroup/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                  <template v-else-if="column.key === 'size'">
                    {{ record.vmcount || 0 }} / {{ record.maxmembers }}
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/autoscalevmgroup' }">{{ $t('label.view.all') || 'View all' }}</router-link>
              </div>
            </a-tab-pane>

            <!-- Networks tab -->
            <a-tab-pane
              key="networks"
              v-if="'listNetworks' in $store.getters.apis"
            >
              <template #tab>
                <ApartmentOutlined /> {{ $t('label.networks') || 'Networks' }}
                <a-badge :count="counts.networks" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="networks"
                :columns="networkColumns"
                :loading="loadingNetworks"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/guestnetwork/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/guestnetwork' }">{{ $t('label.view.all') || 'View all' }}</router-link>
              </div>
            </a-tab-pane>

          </a-tabs>
    </a-card>

    <!-- Backups + Events: side by side -->
    <a-row :gutter="[12, 12]" style="margin-bottom: 12px;">
      <a-col :xs="24" :xl="12"
        v-if="'listBackups' in $store.getters.apis">
        <a-card
          size="small"
          style="margin-bottom: 12px;"
          :loading="loadingBackups"
          v-if="'listBackups' in $store.getters.apis"
        >
          <template #title><SaveOutlined /> {{ $t('label.backups') || 'Latest Backups' }}</template>
          <template #extra>
            <router-link :to="{ path: '/backup' }">{{ $t('label.view.all') || 'View all' }}</router-link>
          </template>
          <a-empty v-if="backups.length === 0" :image="simpleImage" :description="$t('label.no.data') || 'No recent backups'" style="padding: 8px 0;" />
          <div class="backup-list" v-else>
            <div v-for="snap in backups" :key="snap.id" class="backup-row">
              <div class="backup-icon-wrap">
                <SaveOutlined class="backup-icon" />
              </div>
              <div class="backup-body">
                <div class="backup-name">{{ snap.virtualmachinename }}</div>
                <div class="backup-meta">
                  <a-tag :color="snap.status === 'BackedUp' ? 'success' : 'warning'" style="font-size:10px; padding: 0 4px; line-height: 16px;">{{ snap.status }}</a-tag>
                  <span class="backup-type">{{ snap.intervaltype }} · {{ snap.type }}</span>
                  <span class="backup-date">{{ $toLocaleDate(snap.created) }}</span>
                </div>
                <div class="backup-offering">{{ snap.backupofferingname }} · {{ formatSize(snap.size) }}</div>
              </div>
            </div>
          </div>
        </a-card>

      </a-col>
      <a-col :xs="24" :xl="12"
        v-if="'listEvents' in $store.getters.apis">
        <a-card
          size="small"
          style="margin-bottom: 12px;"
          :loading="loadingEvents"
          v-if="'listEvents' in $store.getters.apis"
        >
          <template #title><ScheduleOutlined /> {{ $t('label.events') }}</template>
          <template #extra>
            <router-link :to="{ path: '/event' }">{{ $t('label.view.all') || 'View all' }}</router-link>
          </template>
          <div class="event-list">
            <div
              v-for="event in events"
              :key="event.id"
              class="event-compact-row"
            >
              <span class="event-dot" :class="'dot-' + getEventColour(event)"></span>
              <div class="event-compact-body">
                <div class="event-compact-top">
                  <router-link :to="{ path: '/event/' + event.id }" class="event-type">{{ event.type }}</router-link>
                  <span class="event-compact-time">{{ $toLocaleDate(event.created) }}</span>
                </div>
                <div class="event-compact-desc">
                  <resource-label
                    :resourceType="event.resourcetype"
                    :resourceId="event.resourceid"
                    :resourceName="event.resourcename"
                  />
                </div>
              </div>
            </div>
          </div>
        </a-card>
      </a-col>
    </a-row>

  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import dayjs from 'dayjs'
import { getAPI, postAPI } from '@/api'
import store from '@/store'
import { Empty } from 'ant-design-vue'
import {
  CloudServerOutlined, ClusterOutlined, HddOutlined,
  BuildOutlined, ApartmentOutlined, DeploymentUnitOutlined,
  EnvironmentOutlined, PictureOutlined, ScheduleOutlined,
  SaveOutlined, BarChartOutlined, SyncOutlined
} from '@ant-design/icons-vue'
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js'
import ChartCard from '@/components/widgets/ChartCard'
import ResourceLabel from '@/components/widgets/ResourceLabel'
import Status from '@/components/widgets/Status'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

// ── Usage type IDs ────────────────────────────────────────────────────────
const UT = { RUNNING_VM: 1, IP_ADDRESS: 3, NETWORK_OFFERING: 30 }

// ── Multilingual greetings ────────────────────────────────────────────────
const GREETINGS = [
  'Hello', 'Hola', 'Bonjour', 'Ciao', 'Hallo', 'Hej'
]

// ── Limit definitions ─────────────────────────────────────────────────────
const LIMIT_DEFS = [
  { key: 'vm',               labelKey: 'label.instances',         format: v => String(v)                               },
  { key: 'cpu',              labelKey: 'label.cpunumber',         format: v => String(v)                               },
  { key: 'memory',           labelKey: 'label.memory',            format: v => parseFloat(v / 1024).toFixed(1) + ' G' },
  { key: 'primarystorage',   labelKey: 'label.primary.storage',   format: v => parseFloat(v).toFixed(0) + ' G'        },
  { key: 'secondarystorage', labelKey: 'label.secondary.storage', format: v => parseFloat(v).toFixed(0) + ' G'        },
  { key: 'ip',               labelKey: 'label.public.ips',        format: v => String(v)                               },
  { key: 'network',          labelKey: 'label.networks',          format: v => String(v)                               },
  { key: 'vpc',              labelKey: 'label.vpcs',              format: v => String(v)                               },
  { key: 'volume',           labelKey: 'label.volumes',           format: v => String(v)                               },
  { key: 'snapshot',         labelKey: 'label.snapshots',         format: v => String(v)                               }
]

export default {
  name: 'UserDashboard',

  components: {
    ChartCard, ResourceLabel, Status,
    CloudServerOutlined, ClusterOutlined, HddOutlined,
    BuildOutlined, ApartmentOutlined, DeploymentUnitOutlined,
    EnvironmentOutlined, PictureOutlined, ScheduleOutlined,
    SaveOutlined, BarChartOutlined, SyncOutlined
  },

  props: {
    resource:    { type: Object,  default: () => ({}) },
    showProject: { type: Boolean, default: false       }
  },

  data () {
    // Pick a greeting randomly once per mount
    const greetingWord = GREETINGS[Math.floor(Math.random() * GREETINGS.length)]
    const now          = dayjs()
    const hour         = now.hour()
    let sub = 'Good evening.'
    if (hour < 12) sub = 'Good morning.'
    else if (hour < 18) sub = 'Good afternoon.'

    return {
      greeting:    greetingWord,
      greetingSub: sub,
      simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,

      activeTab: 'vms',

      loadingAccount:  false,
      loadingCounts:   false,
      loadingVms:      false,
      loadingK8s:      false,
      loadingAutoscale: false,
      loadingNetworks: false,
      loadingEvents:   false,
      loadingBackups:  false,
      loadingUsage:    false,

      account: {},
      project: {},

      vms:      [],
      vmTotal:  0,

      k8sClusters: [],
      k8sTotal:    0,

      autoscaleGroups: [],
      autoscaleTotal:  0,

      networks:      [],

      events:  [],
      backups: [],

      usageRecords:  [],
      hasUsageData:  false,
      currentMonthLabel: dayjs().format('MMMM YYYY'),

      counts: {
        running: 0, stopped: 0, kubernetes: 0,
        volumes: 0, snapshots: 0, networks: 0,
        vpcs: 0, ips: 0, templates: 0
      }
    }
  },

  computed: {
    entity () { return this.showProject ? this.project : this.account },

    displayName () {
      return this.$store.getters.userInfo?.firstname ||
             this.$store.getters.userInfo?.username  ||
             this.$store.getters.userInfo?.account   || ''
    },

    limitItems () {
      return LIMIT_DEFS.map(def => {
        const total     = this.entity[def.key + 'total']
        const limit     = this.entity[def.key + 'limit']
        const avail     = this.entity[def.key + 'available']
        const unlimited = (limit === 'Unlimited' || limit === '-1' || limit === undefined)
        const pct       = unlimited ? 0 : Math.min(Math.round((total / limit) * 100), 100)
        return {
          key:          def.key,
          labelKey:     def.labelKey,
          pct:          isNaN(pct) ? 0 : pct,
          usedDisplay:  total !== undefined ? def.format(total) : '—',
          availDisplay: unlimited ? '∞' : (avail !== undefined ? def.format(avail) : '—'),
          limitDisplay: unlimited ? 'Unlimited' : (limit !== undefined ? def.format(limit) : '—')
        }
      }).filter(item => item.usedDisplay !== '—')
    },

    resourceCounts () {
      const apis = this.$store.getters.apis
      return [
        { key: 'running',    apiKey: 'listVirtualMachines',    labelKey: 'label.running.vms',  icon: 'CloudServerOutlined',    count: this.counts.running,    path: '/vm'              },
        { key: 'stopped',    apiKey: 'listVirtualMachines',    labelKey: 'label.stopped.vms',  icon: 'CloudServerOutlined',    count: this.counts.stopped,    path: '/vm'              },
        { key: 'kubernetes', apiKey: 'listKubernetesClusters', labelKey: 'label.kubernetes',   icon: 'ClusterOutlined',        count: this.counts.kubernetes, path: '/kubernetes'      },
        { key: 'volumes',    apiKey: 'listVolumes',            labelKey: 'label.volumes',      icon: 'HddOutlined',            count: this.counts.volumes,    path: '/volume'          },
        { key: 'snapshots',  apiKey: 'listSnapshots',          labelKey: 'label.snapshots',    icon: 'BuildOutlined',          count: this.counts.snapshots,  path: '/snapshot'        },
        { key: 'networks',   apiKey: 'listNetworks',           labelKey: 'label.networks',     icon: 'ApartmentOutlined',      count: this.counts.networks,   path: '/guestnetwork'    },
        { key: 'vpcs',       apiKey: 'listVPCs',               labelKey: 'label.vpcs',         icon: 'DeploymentUnitOutlined', count: this.counts.vpcs,       path: '/vpc'             },
        { key: 'ips',        apiKey: 'listPublicIpAddresses',  labelKey: 'label.public.ips',   icon: 'EnvironmentOutlined',    count: this.counts.ips,        path: '/publicip'        },
        { key: 'templates',  apiKey: 'listTemplates',          labelKey: 'label.templates',    icon: 'PictureOutlined',        count: this.counts.templates,  path: '/template'        }
      ].filter(r => r.apiKey in apis)
    },

    vmColumns () {
      return [
        { title: this.$t('label.name'),      dataIndex: 'name',      key: 'name',      ellipsis: true },
        { title: this.$t('label.state'),     dataIndex: 'state',     key: 'state'                     },
        { title: this.$t('label.resources'), dataIndex: 'cpunumber', key: 'resources'                 },
        { title: this.$t('label.ipaddress'), dataIndex: 'nic',       key: 'ipaddress'                 }
      ]
    },

    k8sColumns () {
      return [
        { title: this.$t('label.name'),    dataIndex: 'name',                    key: 'name'    },
        { title: this.$t('label.state'),   dataIndex: 'state',                   key: 'state'   },
        { title: this.$t('label.size'),    dataIndex: 'size',                    key: 'size'    },
        { title: this.$t('label.zone'),    dataIndex: 'zonename',                key: 'zone',   ellipsis: true },
        { title: 'Version',                dataIndex: 'kubernetesversionname',   key: 'version', ellipsis: true }
      ]
    },

    autoscaleColumns () {
      return [
        { title: this.$t('label.name'),         dataIndex: 'name',     key: 'name'  },
        { title: this.$t('label.state'),         dataIndex: 'state',    key: 'state' },
        { title: this.$t('label.vms') || 'VMs', dataIndex: 'vmcount',  key: 'size'  },
        { title: this.$t('label.zone'),          dataIndex: 'zonename', key: 'zone', ellipsis: true }
      ]
    },

    networkColumns () {
      return [
        { title: this.$t('label.name'),    dataIndex: 'name',    key: 'name'  },
        { title: this.$t('label.state'),   dataIndex: 'state',   key: 'state' },
        { title: this.$t('label.type'),    dataIndex: 'type',    key: 'type'  },
        { title: this.$t('label.cidr'),    dataIndex: 'cidr',    key: 'cidr'  },
        { title: this.$t('label.zone'),    dataIndex: 'zonename',key: 'zone', ellipsis: true }
      ]
    }
  },

  created () {
    this.project = store.getters.project
    this.fetchAll()
    this.$store.watch(
      (state, getters) => getters.project,
      (newValue, oldValue) => {
        if (newValue && newValue.id && (!oldValue || newValue.id !== oldValue.id)) {
          this.fetchAll()
        } else if (store.getters.userInfo.roletype !== 'Admin' && !store.getters.logoutFlag) {
          this.fetchAll()
        }
      }
    )
  },

  watch: {
    '$route' (to)    { if (to.name === 'dashboard') this.fetchAll() },
    resource: {
      deep: true,
      handler (newData) { this.project = newData; if (newData.id) this.fetchAll() }
    },
    '$i18n.global.locale' () { this.fetchAll() },

    // Render chart once data and canvas are both ready
    usageRecords () { nextTick(() => this.renderUsageChart()) }
  },

  mounted () {
    // Expose canvas ref for chart rendering
    this._usageChart = null
  },

  beforeUnmount () {
    if (this._usageChart) { this._usageChart.destroy(); this._usageChart = null }
  },

  methods: {
    fetchAll () {
      this.fetchAccount()
      this.fetchCounts()
      this.fetchVms()
      this.fetchK8s()
      this.fetchAutoscale()
      this.fetchNetworks()
      this.fetchEvents()
      this.fetchBackups()
      this.fetchUsage()
    },

    // ── Account / project ─────────────────────────────────────────────────
    fetchAccount () {
      this.loadingAccount = true
      if (store.getters.project.id) {
        getAPI('listProjects', { id: store.getters.project.id })
          .then(j => { this.project = j?.listprojectsresponse?.project?.[0] || {} })
          .finally(() => { this.loadingAccount = false })
      } else {
        getAPI('listAccounts', { id: this.$store.getters.userInfo.accountid })
          .then(j => { this.account = j?.listaccountsresponse?.account?.[0] || {} })
          .finally(() => { this.loadingAccount = false })
      }
    },

    // ── Count pills ───────────────────────────────────────────────────────
    fetchCounts () {
      this.loadingCounts = true
      const apis = this.$store.getters.apis
      const tiny = { listall: true, page: 1, pagesize: 1 }
      const p = []
      if ('listVirtualMachines' in apis) {
        p.push(
          getAPI('listVirtualMachines', { ...tiny, state: 'running' }).then(j => { this.counts.running    = j?.listvirtualmachinesresponse?.count || 0 }),
          getAPI('listVirtualMachines', { ...tiny, state: 'stopped' }).then(j => { this.counts.stopped    = j?.listvirtualmachinesresponse?.count || 0 })
        )
      }
      if ('listKubernetesClusters' in apis)  p.push(getAPI('listKubernetesClusters', tiny).then(j => { this.counts.kubernetes = j?.listkubernetesclustersresponse?.count || 0 }))
      if ('listVolumes' in apis)             p.push(getAPI('listVolumes', tiny).then(j => { this.counts.volumes    = j?.listvolumesresponse?.count || 0 }))
      if ('listSnapshots' in apis)           p.push(getAPI('listSnapshots', tiny).then(j => { this.counts.snapshots  = j?.listsnapshotsresponse?.count || 0 }))
      if ('listNetworks' in apis)            p.push(getAPI('listNetworks', tiny).then(j => { this.counts.networks   = j?.listnetworksresponse?.count || 0 }))
      if ('listVPCs' in apis)                p.push(getAPI('listVPCs', tiny).then(j => { this.counts.vpcs       = j?.listvpcsresponse?.count || 0 }))
      if ('listPublicIpAddresses' in apis)   p.push(getAPI('listPublicIpAddresses', tiny).then(j => { this.counts.ips        = j?.listpublicipaddressesresponse?.count || 0 }))
      if ('listTemplates' in apis)           p.push(getAPI('listTemplates', { ...tiny, templatefilter: 'self' }).then(j => { this.counts.templates  = j?.listtemplatesresponse?.count || 0 }))
      Promise.allSettled(p).finally(() => { this.loadingCounts = false })
    },

    // ── VMs ───────────────────────────────────────────────────────────────
    fetchVms () {
      if (!('listVirtualMachines' in this.$store.getters.apis)) return
      this.loadingVms = true
      Promise.all([
        getAPI('listVirtualMachines', { listall: true, page: 1, pagesize: 1 }),
        getAPI('listVirtualMachines', { listall: true, page: 1, pagesize: 10 })
      ]).then(([c, l]) => {
        this.vmTotal = c?.listvirtualmachinesresponse?.count || 0
        this.vms     = l?.listvirtualmachinesresponse?.virtualmachine || []
      }).finally(() => { this.loadingVms = false })
    },

    // ── Kubernetes ────────────────────────────────────────────────────────
    fetchK8s () {
      if (!('listKubernetesClusters' in this.$store.getters.apis)) return
      this.loadingK8s = true
      Promise.all([
        getAPI('listKubernetesClusters', { listall: true, page: 1, pagesize: 1 }),
        getAPI('listKubernetesClusters', { listall: true, page: 1, pagesize: 8 })
      ]).then(([c, l]) => {
        this.k8sTotal    = c?.listkubernetesclustersresponse?.count || 0
        this.k8sClusters = l?.listkubernetesclustersresponse?.kubernetescluster || []
      }).finally(() => { this.loadingK8s = false })
    },

    // ── Autoscale groups ──────────────────────────────────────────────────
    fetchAutoscale () {
      if (!('listAutoScaleVmGroups' in this.$store.getters.apis)) return
      this.loadingAutoscale = true
      Promise.all([
        getAPI('listAutoScaleVmGroups', { listall: true, page: 1, pagesize: 1 }),
        getAPI('listAutoScaleVmGroups', { listall: true, page: 1, pagesize: 8 })
      ]).then(([c, l]) => {
        this.autoscaleTotal  = c?.listautoscalevmgroupsresponse?.count || 0
        this.autoscaleGroups = l?.listautoscalevmgroupsresponse?.autoscalevmgroup || []
      }).finally(() => { this.loadingAutoscale = false })
    },

    // ── Networks ──────────────────────────────────────────────────────────
    fetchNetworks () {
      if (!('listNetworks' in this.$store.getters.apis)) return
      this.loadingNetworks = true
      getAPI('listNetworks', { listall: true, page: 1, pagesize: 8 })
        .then(j => { this.networks = j?.listnetworksresponse?.network || [] })
        .finally(() => { this.loadingNetworks = false })
    },

    // ── Events ────────────────────────────────────────────────────────────
    fetchEvents () {
      if (!('listEvents' in this.$store.getters.apis)) return
      this.loadingEvents = true
      getAPI('listEvents', { page: 1, pagesize: 12, listall: true })
        .then(j => { this.events = j?.listeventsresponse?.event || [] })
        .finally(() => { this.loadingEvents = false })
    },

    // ── Backups: try VM snapshots first, fall back to volume snapshots ─────
    fetchBackups () {
      if (!('listBackups' in this.$store.getters.apis)) return
      this.loadingBackups = true
      getAPI('listBackups', { listall: true, page: 1, pagesize: 6 })
        .then(j => { this.backups = j?.listbackupsresponse?.backup || [] })
        .finally(() => { this.loadingBackups = false })
    },

    // ── Mini usage chart (VM hours + IP hours + Network hours) ────────────
    fetchUsage () {
      if (!('listUsageRecords' in this.$store.getters.apis)) return
      this.loadingUsage = true
      const month = dayjs().startOf('month')
      const params = {
        startdate: month.format('YYYY-MM-DD HH:mm:ss'),
        enddate:   month.endOf('month').format('YYYY-MM-DD HH:mm:ss'),
        pagesize:  1, page: 1
      }
      // scope to current account/project
      const projectId = store.getters.project?.id
      if (projectId) {
        params.projectid = projectId
      } else {
        params.accountid = this.$store.getters.userInfo.accountid
      }

      postAPI('listUsageRecords', params).then(async json => {
        const total = json?.listusagerecordsresponse?.count || 0
        if (total === 0) { this.hasUsageData = false; return }

        const pageSize = 500
        const pages = await Promise.all(
          Array.from({ length: Math.ceil(total / pageSize) }, (_, i) =>
            postAPI('listUsageRecords', { ...params, page: i + 1, pagesize: pageSize })
              .then(j => j?.listusagerecordsresponse?.usagerecord || [])
              .catch(() => [])
          )
        )
        this.usageRecords = pages.flat()
        this.hasUsageData = this.usageRecords.length > 0
      }).finally(() => { this.loadingUsage = false })
    },

    renderUsageChart () {
      const canvas = this.$refs.usageChartCanvas
      if (!canvas || !this.hasUsageData) return
      if (this._usageChart) { this._usageChart.destroy(); this._usageChart = null }

      const month = dayjs().startOf('month')
      const days  = month.daysInMonth()

      // Build daily buckets — only hour-based types
      const labels   = []
      const vmHours  = []
      const ipHours  = []
      const netHours = []

      for (let d = 1; d <= days; d++) {
        const dateStr  = month.date(d).format('YYYY-MM-DD')
        const recs     = this.usageRecords.filter(r => r.startdate?.startsWith(dateStr))
        const sum      = (type) => recs.filter(r => r.usagetype === type).reduce((s, r) => s + parseFloat(r.rawusage || 0), 0)
        labels.push(String(d))
        vmHours.push( Math.round(sum(UT.RUNNING_VM)       * 10) / 10)
        ipHours.push( Math.round(sum(UT.IP_ADDRESS)       * 10) / 10)
        netHours.push(Math.round(sum(UT.NETWORK_OFFERING) * 10) / 10)
      }

      this._usageChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            { label: 'VM Hours',      data: vmHours,  backgroundColor: 'rgba(24,  144, 255, 0.75)', borderWidth: 0, borderRadius: 2 },
            { label: 'IP Hours',      data: ipHours,  backgroundColor: 'rgba( 82, 196,  26, 0.75)', borderWidth: 0, borderRadius: 2 },
            { label: 'Network Hours', data: netHours, backgroundColor: 'rgba(250, 173,  20, 0.75)', borderWidth: 0, borderRadius: 2 }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { boxWidth: 10, padding: 12, font: { size: 11 } } },
            tooltip: { mode: 'index', intersect: false }
          },
          scales: {
            x: { stacked: true, grid: { display: false }, ticks: { maxRotation: 0, font: { size: 10 } } },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } }
          }
        }
      })
    },

    // ── Helpers ────────────────────────────────────────────────────────────
    getBarColor  (pct) { return pct >= 90 ? '#ff4d4f' : pct >= 75 ? '#faad14' : '#52c41a' },
    getUsedClass (pct) { return pct >= 90 ? 'used-critical' : pct >= 75 ? 'used-warning' : '' },
    getEventColour (event) {
      if (event.level === 'ERROR')     return 'red'
      if (event.state === 'Completed') return 'green'
      return 'blue'
    },
    formatMemory (mb) {
      if (!mb) return '0 MB'
      return mb >= 1024 ? (mb / 1024).toFixed(1) + ' GB' : mb + ' MB'
    },

    formatSize (bytes) {
      if (!bytes) return '0 B'
      if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB'
      if (bytes >= 1048576)    return (bytes / 1048576).toFixed(1) + ' MB'
      if (bytes >= 1024)       return (bytes / 1024).toFixed(1) + ' KB'
      return bytes + ' B'
    }
  }
}
</script>

<style lang="less" scoped>
// ── Greeting ──────────────────────────────────────────────────────────────
.greeting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.greeting-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 2px;
  color: rgba(0, 0, 0, 0.85);
}

.greeting-sub {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0;
}

// ── Limit bars ────────────────────────────────────────────────────────────
.limits-row {
  display: flex;
  flex-direction: row;
  width: 100%;
  overflow: hidden;
}

.limit-item {
  flex: 1 1 0;
  min-width: 0;
  padding: 0 10px;
  border-right: 1px solid #f0f0f0;
  &:first-child { padding-left: 0; }
  &:last-child  { padding-right: 0; border-right: none; }
}

.limit-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
}

.limit-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
}

.limit-used {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  flex-shrink: 0;
  &.used-warning  { color: #faad14; font-weight: 600; }
  &.used-critical { color: #ff4d4f; font-weight: 600; }
}

.limit-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.limit-max { color: #bfbfbf; }

// ── Count pills ───────────────────────────────────────────────────────────
.counts-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.count-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 20px;
  background: #fafafa;
  color: rgba(0, 0, 0, 0.65);
  text-decoration: none;
  transition: border-color 0.2s, background 0.2s;
  white-space: nowrap;
  &:hover { border-color: #1890ff; background: #e6f7ff; color: #1890ff; }
}

.count-icon  { font-size: 13px; opacity: 0.65; }
.count-value { font-size: 15px; font-weight: 700; color: rgba(0, 0, 0, 0.85); line-height: 1; }
.count-label { font-size: 12px; }

// ── Tabbed table ──────────────────────────────────────────────────────────
.tab-footer {
  padding: 8px 12px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  text-align: center;
}

:deep(.ant-tabs-nav) { margin-bottom: 0 !important; }
:deep(.ant-tabs-content-holder) { padding-bottom: 0; }

// ── Compact events ────────────────────────────────────────────────────────
.event-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.event-compact-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
}

.event-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  &.dot-red   { background: #ff4d4f; }
  &.dot-green { background: #52c41a; }
  &.dot-blue  { background: #1890ff; }
}

.event-compact-body { flex: 1; min-width: 0; }

.event-compact-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px;
}

.event-type {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 65%;
}

.event-compact-time {
  font-size: 10px;
  color: #bbb;
  white-space: nowrap;
  flex-shrink: 0;
}

.event-compact-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// ── Backups ───────────────────────────────────────────────────────────────
.backup-list { display: flex; flex-direction: column; gap: 0; }

.backup-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
}

.backup-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f0f5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.backup-icon { color: #1890ff; font-size: 13px; }

.backup-body { flex: 1; min-width: 0; }

.backup-name {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.backup-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  overflow: hidden;
}

.backup-offering {
  font-size: 10px;
  color: #bbb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.backup-type { color: rgba(0, 0, 0, 0.35); font-size: 10px; }
.backup-vm   { color: rgba(0, 0, 0, 0.45); }
.backup-date { color: #bbb; margin-left: auto; }

// ── Shared ────────────────────────────────────────────────────────────────
.bordered-card { border-radius: 5px; flex: 1; height: 100%; }
.ant-col { display: flex; flex-flow: column; padding-bottom: 5px; }
</style>